// =============================================================================
// VibeSelectorCompact — 5-chip vibe picker for the Command Center
// =============================================================================
// Sits directly below the orb. Active vibe highlighted in persona color.
// Vibes locked for the active persona are dimmed and show a tooltip
// (PERSONA_LOCK_MESSAGES from lib/vibes.ts). Clicking a locked chip is a
// no-op — clicking an allowed chip fires onChange and persists via the
// caller's PATCH /api/settings flow.
// =============================================================================

import { Lock } from 'lucide-react'
import { cn } from '@/lib/cn'
import {
  VIBE_DEFS,
  isAllowed,
  lockMessage,
  type Vibe,
  type VibePersona,
} from '@/lib/vibes'

const VIBE_ORDER: Vibe[] = ['professional', 'casual', 'direct', 'hype', 'custom']

interface VibeSelectorCompactProps {
  currentVibe: Vibe
  personaId: VibePersona
  onChange: (vibe: Vibe) => void
  /** Render a smaller variant (chips a bit tighter). Optional. */
  dense?: boolean
}

export function VibeSelectorCompact({
  currentVibe,
  personaId,
  onChange,
  dense = false,
}: VibeSelectorCompactProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-center gap-1.5',
        dense ? 'text-xs' : 'text-sm',
      )}
      role="radiogroup"
      aria-label="Greeting vibe"
    >
      {VIBE_ORDER.map((vibe) => {
        const def = VIBE_DEFS[vibe]
        const allowed = isAllowed(personaId, vibe)
        const locked = !allowed
        const active = currentVibe === vibe && allowed
        const tooltip = locked
          ? lockMessage(personaId, vibe) ?? 'Not available for this persona'
          : def.tagline

        return (
          <button
            key={vibe}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={locked}
            title={tooltip}
            onClick={() => {
              if (!locked) onChange(vibe)
            }}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 transition-colors',
              dense ? 'py-0.5 px-2.5 text-xs' : 'text-xs',
              active
                ? 'border-jarvis-blue/60 bg-jarvis-blue/15 text-jarvis-blue'
                : 'border-jarvis-border text-jarvis-muted hover:text-jarvis-text hover:bg-jarvis-surface-2',
              locked && 'opacity-40 cursor-not-allowed hover:bg-transparent',
            )}
          >
            <span>{def.label}</span>
            {locked && <Lock size={10} className="ml-0.5" aria-hidden />}
          </button>
        )
      })}
    </div>
  )
}
